import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, UserPlus, ShieldCheck, ArrowRight, Mail, Phone, ExternalLink } from 'lucide-react';
import { BRAND } from '../mock/mock';

const actions = [
  {
    to: '/apply',
    icon: FileText,
    eyebrow: 'For Students',
    title: 'Apply Now',
    desc: 'Start a new student application. Takes less than 2 minutes to submit your details and documents.',
    accent: 'gold',
    testid: 'home-apply',
    secondary: { to: '/apply/domestic', label: 'Domestic application form' },
  },
  {
    to: '/recruiter-signup',
    icon: UserPlus,
    eyebrow: 'For New Partners',
    title: 'Recruiter Sign Up',
    desc: 'Join our partner network. Submit your ID and business license for approval.',
    accent: 'green',
    testid: 'home-recruiter-signup',
    secondary: { to: '/recruiter/login', label: 'Already a partner? Sign in' },
  },
  {
    to: '/admin-login',
    icon: ShieldCheck,
    eyebrow: 'For Staff',
    title: 'Admin Login',
    desc: 'Authorized staff access to manage applications, partners and student records.',
    accent: 'dark',
    testid: 'home-admin-login',
  },
];

const Home = () => {
  return (
    <main className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f7fbf3] via-[#fdfaf1] to-[#f4f9ef]" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#F0B93C]/15 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#0B5E2A]/15 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="relative">
        <div className="max-w-6xl mx-auto container-px h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="HCC" className="h-11 w-11 rounded-full object-contain" />
            <div className="leading-tight">
              <div className="font-display text-[18px] font-bold brand-green">{BRAND.name}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gold font-semibold">{BRAND.full}</div>
            </div>
          </Link>
          <div className="hidden sm:flex items-center gap-5 text-xs text-[#4b5e4d]">
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-1.5 hover:text-[#0B5E2A] transition-colors"><Mail className="h-3.5 w-3.5" /> {BRAND.email}</a>
            <span className="h-3 w-px bg-[#0B5E2A]/20" />
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-1.5 hover:text-[#0B5E2A] transition-colors"><Phone className="h-3.5 w-3.5" /> {BRAND.phone}</a>
            <span className="h-3 w-px bg-[#0B5E2A]/20" />
            <a
              href="https://www.hccbc.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-hcc-website-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0B5E2A] text-white font-semibold hover:bg-[#0a4d24] transition-colors"
            >
              Go to main HCC website <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero + Cards */}
      <section className="flex-1 flex items-center">
        <div className="max-w-6xl mx-auto container-px w-full py-12 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow fade-up">Welcome to the Portal</span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl text-[#0B5E2A] leading-[1.05] fade-up">
              Start Your Journey <br className="hidden sm:block" />
              with <span className="text-brand-gold">Heritage</span>
            </h1>
            <p className="mt-5 text-[#4b5e4d] text-lg leading-relaxed fade-up-delay-1">
              Choose an option below to continue. Students can apply, partners can register, and staff can sign in to the admin console.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 fade-up-delay-2 max-w-5xl mx-auto justify-items-stretch">
            {actions.map((a) => (
              <ActionCard key={a.to} {...a} />
            ))}
          </div>

          <div className="mt-12 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-[#0B5E2A]/15 backdrop-blur text-xs text-[#4b5e4d]">
              <span className="h-2 w-2 rounded-full bg-[#0B5E2A] animate-pulse" />
              Secure portal · SSL encrypted · Data handled per privacy policy
            </div>
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="relative border-t border-[#0B5E2A]/10 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto container-px py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#4b5e4d]">
          <div>© {new Date().getFullYear()} Heritage Community College. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-[#0B5E2A] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#0B5E2A] transition-colors">Terms</a>
            <a href="#" className="hover:text-[#0B5E2A] transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </main>
  );
};

const ActionCard = ({ to, icon: Icon, eyebrow, title, desc, accent, secondary, testid }) => {
  const accentStyles = {
    gold: {
      badge: 'bg-brand-gold text-[#06401d]',
      ring: 'group-hover:border-[#F0B93C]',
      cta: 'text-[#9a6a0b] group-hover:text-[#6f4b05]',
    },
    green: {
      badge: 'bg-[#0B5E2A] text-white',
      ring: 'group-hover:border-[#0B5E2A]',
      cta: 'text-[#0B5E2A] group-hover:text-[#06401d]',
    },
    dark: {
      badge: 'bg-[#06401d] text-white',
      ring: 'group-hover:border-[#06401d]',
      cta: 'text-[#06401d] group-hover:text-black',
    },
  }[accent];

  return (
    <div className={`group relative bg-white border border-[#0B5E2A]/10 ${accentStyles.ring} rounded-2xl p-6 card-lift overflow-hidden flex flex-col`}>
      {/* Full-card clickable overlay — raised above decorative visuals so any click on the card hits the Link */}
      <Link to={to} data-testid={testid} className="absolute inset-0 z-10" aria-label={title} />
      {/* Decorative blob — never intercepts clicks */}
      <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#F0B93C]/10 group-hover:bg-[#F0B93C]/25 transition-colors pointer-events-none" />
      {/* Visual content — pointer-events-none lets clicks fall through to the overlay Link */}
      <div className={`relative h-12 w-12 rounded-xl ${accentStyles.badge} flex items-center justify-center shadow-sm pointer-events-none`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="relative mt-5 flex-1 pointer-events-none">
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#6b7e6d]">{eyebrow}</div>
        <h3 className="mt-1.5 font-display text-xl font-bold text-[#0B5E2A]">{title}</h3>
        <p className="mt-2 text-[13.5px] text-[#4b5e4d] leading-relaxed">{desc}</p>
      </div>
      <div className={`relative mt-4 inline-flex items-center gap-2 text-sm font-semibold pointer-events-none ${accentStyles.cta}`}>
        Continue
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
      {secondary && (
        <Link
          to={secondary.to}
          className="relative mt-3 text-xs text-[#6b7e6d] hover:text-[#0B5E2A] underline decoration-dotted underline-offset-2 z-20 self-start"
        >
          {secondary.label} →
        </Link>
      )}
    </div>
  );
};

export default Home;
